/**
 * MiniAppEditor.js
 * Sales Studio editor for Mini-Apps: the user describes the app, the AI builds
 * it for a real test contact, the user saves the result as the blueprint and
 * can publish it for that one contact. In emails the play travels as
 * {{image:token}}: every recipient gets their own app, shown as a clickable
 * preview card.
 */

import React, { useEffect, useMemo, useState } from "react";
import { ref, get, push, update, remove } from "firebase/database";
import { database } from "../firebaseConfig";
import {
  authenticatedFetch,
  CLOUD_FUNCTION_URLS,
} from "../utils/cloudFunctions";
import { costOf } from "../config/creditPlans";
import {
  buildImageContextText,
  buildImagePromptVariables,
} from "./EmailAiModes/dynamicImageUtils";
import { loadDemoContacts, pickRandomContacts } from "./salesPlayDemo";
import MiniAppRenderer from "../miniApps/MiniAppRenderer";
import {
  applyBrand,
  readableAccent,
  sanitizeDesign,
} from "../miniApps/miniAppDesign";
import TEMPLATES from "../miniApps/miniAppTemplates.json";
import "./ImageLab.css";
import "./MiniAppEditor.css";

const coverOf = (template) => `/img/sales-studio/miniapps/${template.id}.webp`;
const DEMO_CTA_URL = "https://cal.com";

const THEMES = [
  { id: "light", label: "Light", swatch: ["#ffffff", "#0f1720"] },
  { id: "dark", label: "Dark", swatch: ["#0b0f12", "#eef2f5"] },
  { id: "warm", label: "Warm", swatch: ["#fbf8f3", "#1f1a14"] },
];
const ACCENTS = [
  "#2a6776",
  "#1e3a5f",
  "#3b3f8f",
  "#5b2a86",
  "#6b2147",
  "#9a3412",
  "#14532d",
  "#374151",
];
const FONTS = [
  { id: "modern", label: "Modern" },
  { id: "editorial", label: "Editorial" },
];
const CORNERS = [
  { id: "sharp", label: "Sharp" },
  { id: "soft", label: "Soft" },
  { id: "round", label: "Round" },
];
const LAYOUTS = [
  { id: "split", label: "Side by side" },
  { id: "stacked", label: "Result on top" },
];
const SECTIONS = [
  { id: "insight", label: "Key insight" },
  { id: "scenarios", label: "Scenarios" },
  { id: "ticker", label: "Live counter" },
  { id: "projection", label: "Projection" },
  { id: "chart", label: "Chart" },
  { id: "comparison", label: "Comparison" },
  { id: "checklist", label: "Checklist" },
  { id: "footnote", label: "Footnote" },
  { id: "sender", label: "Your name" },
];

const Segmented = ({ label, options, value, onChange }) => (
  <div className="mae-designRow">
    <span>{label}</span>
    <div className="mae-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          type="button"
          key={o.id}
          role="radio"
          aria-checked={value === o.id}
          className={value === o.id ? "on" : ""}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const BRAND_MODES = [
  {
    id: "full",
    label: "Their brand",
    hint: "Color, logo, light or dark, font and corners from each contact's website.",
  },
  {
    id: "colors",
    label: "Their colors",
    hint: "Their brand color and logo, your layout.",
  },
  {
    id: "off",
    label: "Your design",
    hint: "The same look for every contact.",
  },
];

const BrandFound = ({ brandStyle, business }) => {
  if (!brandStyle) {
    return (
      <p className="il-fieldHint">
        No brand found for {business}. Your settings below are used.
      </p>
    );
  }
  return (
    <div className="mae-brandFound">
      <span className="mae-brandFoundHead">From {business}'s website</span>
      <div className="mae-brandFoundRow">
        {brandStyle.logoUrl && (
          <span
            className={`mae-brandLogo ${brandStyle.logoOnDark ? "onDark" : ""}`}
            style={
              brandStyle.logoOnDark ? { background: brandStyle.accent } : null
            }
          >
            <img src={brandStyle.logoUrl} alt="" />
          </span>
        )}
        <span className="mae-brandSwatches">
          {[
            brandStyle.accent,
            ...brandStyle.colors.filter((c) => c !== brandStyle.accent),
          ]
            .filter(Boolean)
            .slice(0, 4)
            .map((c) => (
              <i key={c} style={{ background: c }} title={c} />
            ))}
        </span>
      </div>
      {brandStyle.mood && (
        <span className="mae-brandMood">{brandStyle.mood}</span>
      )}
    </div>
  );
};

const DesignPanel = ({ design, onChange, spec, brandStyle, brandFor }) => {
  const set = (patch) =>
    onChange((prev) =>
      sanitizeDesign({
        ...prev,
        ...(typeof patch === "function" ? patch(prev) : patch),
      }),
    );
  const [custom, setCustom] = useState(
    design.accent && !ACCENTS.includes(design.accent)
      ? design.accent
      : "#2a6776",
  );
  const [tooLight, setTooLight] = useState(false);
  const present = new Set(
    [
      spec?.insight && "insight",
      spec?.scenarios && "scenarios",
      spec?.ticker && "ticker",
      spec?.projection && "projection",
      spec?.chart && "chart",
      spec?.comparison && "comparison",
      spec?.checklist && "checklist",
      spec?.footnote && "footnote",
      "sender",
    ].filter(Boolean),
  );
  const pickCustom = (hex) => {
    setCustom(hex);
    const ok = readableAccent(hex, "");
    setTooLight(!ok);
    if (ok) set({ accent: ok });
  };

  return (
    <div className="mae-design">
      <span className="il-fieldLabel">Design</span>

      <div
        className="mae-brandModes"
        role="radiogroup"
        aria-label="Brand match"
      >
        {BRAND_MODES.map((m) => (
          <button
            type="button"
            key={m.id}
            role="radio"
            aria-checked={design.brand === m.id}
            className={`mae-brandMode ${design.brand === m.id ? "on" : ""}`}
            onClick={() => set({ brand: m.id })}
          >
            <b>{m.label}</b>
            <span>{m.hint}</span>
          </button>
        ))}
      </div>
      {design.brand !== "off" && brandFor && (
        <BrandFound brandStyle={brandStyle} business={brandFor} />
      )}
      {design.brand !== "off" && (
        <p className="il-fieldHint">
          {design.brand === "full"
            ? "The settings below apply when a contact has no website we can read."
            : "Theme, font and corners below stay yours. The color is theirs."}
        </p>
      )}

      <div className="mae-designRow">
        <span>Theme</span>
        <div className="mae-themes" role="radiogroup" aria-label="Theme">
          {THEMES.map((t) => (
            <button
              type="button"
              key={t.id}
              role="radio"
              aria-checked={design.theme === t.id}
              className={`mae-theme ${design.theme === t.id ? "on" : ""}`}
              onClick={() => set({ theme: t.id })}
            >
              <span
                className="mae-themeSwatch"
                style={{ background: t.swatch[0] }}
              >
                <i style={{ background: t.swatch[1] }} />
                <i style={{ background: design.accent || "#2a6776" }} />
              </span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mae-designRow">
        <span>Accent</span>
        <div
          className="mae-accents"
          role="radiogroup"
          aria-label="Accent color"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!design.accent}
            className={`mae-accentAuto ${!design.accent ? "on" : ""}`}
            title="The AI picks a color that fits each prospect"
            onClick={() => set({ accent: "" })}
          >
            Auto
          </button>
          {ACCENTS.map((hex) => (
            <button
              type="button"
              key={hex}
              role="radio"
              aria-checked={design.accent === hex}
              aria-label={hex}
              className={`mae-accent ${design.accent === hex ? "on" : ""}`}
              style={{ background: hex }}
              onClick={() => set({ accent: hex })}
            />
          ))}
          <label
            className={`mae-accent mae-accentCustom ${design.accent && !ACCENTS.includes(design.accent) ? "on" : ""}`}
            title="Your own color"
            style={{ background: custom }}
          >
            <input
              type="color"
              value={custom}
              onChange={(e) => pickCustom(e.target.value)}
              aria-label="Custom color"
            />
            <i className="fa-solid fa-eye-dropper" aria-hidden="true" />
          </label>
        </div>
      </div>
      {tooLight && (
        <p className="il-fieldHint mae-warn">
          Too light for white button text. Pick a darker shade.
        </p>
      )}

      <Segmented
        label="Font"
        options={FONTS}
        value={design.font}
        onChange={(font) => set({ font })}
      />
      <Segmented
        label="Corners"
        options={CORNERS}
        value={design.corners}
        onChange={(corners) => set({ corners })}
      />
      <Segmented
        label="Layout"
        options={LAYOUTS}
        value={design.layout}
        onChange={(layout) => set({ layout })}
      />

      <div className="mae-designRow">
        <span>Show</span>
        <div className="mae-shows">
          {SECTIONS.filter((s) => present.has(s.id)).map((s) => {
            const shown = !design.hide.includes(s.id);
            return (
              <button
                type="button"
                key={s.id}
                aria-pressed={shown}
                className={`mae-show ${shown ? "on" : ""}`}
                onClick={() =>
                  set((prev) => ({
                    hide: prev.hide.includes(s.id)
                      ? prev.hide.filter((h) => h !== s.id)
                      : [...prev.hide, s.id],
                  }))
                }
              >
                <i
                  className={`fa-solid ${shown ? "fa-check" : "fa-plus"}`}
                  aria-hidden="true"
                />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const BRAIN_MODELS = [
  {
    id: "fast",
    label: "Fast",
    engine: "GPT-5.6 Luna",
    action: "miniAppBrainFast",
    hint: "Short texts, sorting, summaries",
  },
  {
    id: "writer",
    label: "Writer",
    engine: "Claude Sonnet 5.5",
    action: "miniAppBrainWriter",
    hint: "Arguments and texts with nuance",
  },
  {
    id: "web",
    label: "Web research",
    engine: "Grok 4.3, live web",
    action: "miniAppBrainWeb",
    hint: "Current facts about them and their market",
  },
  {
    id: "top",
    label: "Deep thinker",
    engine: "Claude Opus 5.5",
    action: "miniAppBrainTop",
    hint: "Hard analysis, strategy, many sources",
  },
];
const MAX_BRAIN_TASKS = 3;
const BRAIN_PRESETS = [
  {
    label: "Market check",
    model: "web",
    prompt:
      "Research this business and its local market: the main competitors nearby, their prices or ratings where public, and one thing this business does better or worse than them. Name the sources.",
  },
  {
    label: "Pain points",
    model: "writer",
    prompt:
      "Name the three most expensive problems this business likely has in the area our offer solves. For each, give a rough yearly cost for them and how you got to it.",
  },
  {
    label: "Personal opener",
    model: "writer",
    prompt:
      "Write two sentences that show we looked closely at this business, based on something specific from their website or reviews. No flattery.",
  },
];
const brainCreditsOf = (model) =>
  costOf((BRAIN_MODELS.find((m) => m.id === model) || BRAIN_MODELS[1]).action);
const newTaskId = () =>
  `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const asTaskList = (raw) =>
  (Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? Object.values(raw)
      : []
  )
    .filter((t) => t && typeof t === "object")
    .map((t) => ({
      id: t.id || newTaskId(),
      label: t.label || "",
      prompt: t.prompt || "",
      model: BRAIN_MODELS.some((m) => m.id === t.model) ? t.model : "writer",
    }));

const BrainPanel = ({ tasks, onChange }) => {
  const patch = (id, change) =>
    onChange((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...change } : t)),
    );
  const add = (preset) =>
    onChange((prev) =>
      prev.length >= MAX_BRAIN_TASKS
        ? prev
        : [
            ...prev,
            {
              id: newTaskId(),
              label: preset?.label || "",
              prompt: preset?.prompt || "",
              model: preset?.model || "writer",
            },
          ],
    );
  const remove = (id) => onChange((prev) => prev.filter((t) => t.id !== id));

  return (
    <div className="mae-brain">
      {tasks.map((task, index) => (
        <div className="mae-brainTask" key={task.id}>
          <div className="mae-brainHead">
            <span className="mae-brainNum">{index + 1}</span>
            <input
              type="text"
              className="mae-brainLabel"
              value={task.label}
              maxLength={40}
              placeholder="Name, e.g. Market check"
              aria-label={`Name of content task ${index + 1}`}
              onChange={(e) => patch(task.id, { label: e.target.value })}
            />
            <button
              type="button"
              className="wr-pageRemove"
              aria-label={`Remove content task ${index + 1}`}
              onClick={() => remove(task.id)}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div
            className="mae-brainModels"
            role="radiogroup"
            aria-label="AI for this task"
          >
            {BRAIN_MODELS.map((m) => (
              <button
                type="button"
                key={m.id}
                role="radio"
                aria-checked={task.model === m.id}
                className={`mae-brainModel ${task.model === m.id ? "active" : ""}`}
                onClick={() => patch(task.id, { model: m.id })}
                title={m.hint}
              >
                <b>{m.label}</b>
                <span>{m.engine}</span>
                <em>{brainCreditsOf(m.id)} credits</em>
              </button>
            ))}
          </div>
          <textarea
            className="il-aiPromptInput"
            rows={3}
            value={task.prompt}
            maxLength={1500}
            placeholder="What should this AI work out or write for each prospect?"
            aria-label={`Prompt of content task ${index + 1}`}
            onChange={(e) => patch(task.id, { prompt: e.target.value })}
          />
        </div>
      ))}
      {tasks.length < MAX_BRAIN_TASKS && (
        <div className="mae-brainAdd">
          <button type="button" className="wr-pageAdd" onClick={() => add()}>
            <i className="fa-solid fa-plus" />
            Add content task
            <span>up to {MAX_BRAIN_TASKS}</span>
          </button>
          <div className="mae-brainPresets">
            <span className="il-fieldHint">Or start from</span>
            {BRAIN_PRESETS.filter(
              (p) => !tasks.some((t) => t.label === p.label),
            ).map((p) => (
              <button
                type="button"
                key={p.label}
                className="wr-demoFrame"
                onClick={() => add(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Step = ({ n, title, sub, children }) => (
  <section className="wr-step">
    <div className="wr-stepHead">
      <span className="wr-stepNum">{n}</span>
      <div>
        <h3 className="wr-stepTitle">{title}</h3>
        <p className="wr-stepSub">{sub}</p>
      </div>
    </div>
    {children}
  </section>
);

const slugifyToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

const uniqueToken = (base, taken) => {
  let token = base;
  let n = 2;
  while (taken.has(token)) token = `${base}_${n++}`;
  return token;
};

const hostOf = (url) => {
  try {
    return new URL(
      /^https?:\/\//i.test(url) ? url : `https://${url}`,
    ).hostname.replace(/^www\./, "");
  } catch (_) {
    return "";
  }
};

const callMiniApp = async (payload) => {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.miniApp(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok) return data;
  if (res.status === 402) {
    throw new Error(
      `Not enough credits (needs ${data.creditsRequired ?? "a few"}, balance ${data.creditBalance ?? 0}).`,
    );
  }
  throw new Error(
    data.details || data.error || "Something went wrong. Please try again.",
  );
};

const MiniAppEditor = ({ userId, assetId = null, assets = [], onExit }) => {
  const existing = useMemo(
    () => assets.find((a) => a.id === assetId) || null,
    [assets, assetId],
  );
  const [name, setName] = useState(existing?.name || "");
  const [token, setToken] = useState(existing?.token || "");
  const [tokenTouched, setTokenTouched] = useState(!!existing);
  const [prompt, setPrompt] = useState(existing?.prompt || "");
  const [ctaUrl, setCtaUrl] = useState(existing?.ctaUrl || "");
  const [blueprint, setBlueprint] = useState(existing?.blueprint || null);
  const [design, setDesign] = useState(() =>
    sanitizeDesign(existing ? existing.design : { brand: "full" }),
  );
  const [savedDesign, setSavedDesign] = useState(() =>
    sanitizeDesign(existing?.design),
  );
  const [savedId, setSavedId] = useState(assetId);
  const [brainTasks, setBrainTasks] = useState(() =>
    asTaskList(existing?.brain),
  );

  const [settings, setSettings] = useState({});
  const [pool, setPool] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(null);

  const [draft, setDraft] = useState(null);
  const [draftVersion, setDraftVersion] = useState(0);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [publishError, setPublishError] = useState("");
  const [published, setPublished] = useState(null);
  const [copied, setCopied] = useState("");
  const [templateId, setTemplateId] = useState(null);

  const activeTasks = brainTasks
    .filter((t) => t.prompt.trim())
    .map((t, i) => ({
      ...t,
      label: t.label.trim() || `Content ${i + 1}`,
      prompt: t.prompt.trim(),
    }));
  const brainCredits = activeTasks.reduce(
    (sum, t) => sum + brainCreditsOf(t.model),
    0,
  );
  const credits = Math.round((costOf("miniApp") + brainCredits) * 100) / 100;
  const otherTokens = useMemo(
    () =>
      new Set(
        assets.filter((a) => a.id !== savedId && a.token).map((a) => a.token),
      ),
    [assets, savedId],
  );
  const effectiveToken = uniqueToken(
    slugifyToken(token) || `app_${slugifyToken(name) || "mini"}`,
    otherTokens,
  );
  const contact = contacts.find((c) => c.id === contactId) || null;
  const template = TEMPLATES.find((t) => t.id === templateId) || null;
  const showingDemo = !draft && !!template && blueprint === template.demo;
  const previewSpec = useMemo(() => {
    const base = draft ? draft.spec : blueprint;
    if (!base) return null;
    if (!showingDemo) {
      return {
        ...base,
        design: draft ? applyBrand(design, draft.brandStyle) : design,
      };
    }
    const url = /^(https:\/\/|mailto:)/i.test(ctaUrl.trim())
      ? ctaUrl.trim()
      : DEMO_CTA_URL;
    return {
      ...base,
      design,
      cta: base.cta ? { ...base.cta, url } : null,
      sender: {
        name: settings.user_name || "",
        company: settings.company_name || "",
      },
    };
  }, [draft, showingDemo, blueprint, ctaUrl, settings, design]);

  useEffect(() => {
    let alive = true;
    get(ref(database, `/World/${userId}/settings/email`))
      .then((snap) => alive && setSettings(snap.val() || {}))
      .catch(() => {});
    loadDemoContacts(userId)
      .then((list) => {
        if (!alive) return;
        setPool(list);
        setContacts(pickRandomContacts(list));
      })
      .catch(() => alive && setPool([]));
    return () => {
      alive = false;
    };
  }, [userId]);

  const pickTemplate = (t) => {
    setTemplateId(t.id);
    setPrompt(t.prompt);
    if (!name.trim() || TEMPLATES.some((x) => x.name === name)) {
      setName(t.name);
      if (!tokenTouched) setToken(`app_${slugifyToken(t.name)}`);
    }
    setBlueprint(t.demo);
    if (t.brain && !brainTasks.some((b) => b.prompt.trim())) {
      setBrainTasks(asTaskList(t.brain));
    }
    setDraft(null);
    setPublished(null);
    setTestMessage("");
    setDraftVersion((v) => v + 1);
  };

  const payloadFor = (c) => ({
    userId,
    contactId: c.id,
    variables: buildImagePromptVariables(c, { userSettings: settings }),
    contextText: buildImageContextText(c),
  });

  const build = async () => {
    if (!contact) {
      setTestMessage("Pick a test contact first.");
      return;
    }
    if (!prompt.trim()) {
      setTestMessage("Describe what the Mini-App should do in step 1.");
      return;
    }
    setBusy("build");
    setTestMessage("");
    setPublishError("");
    setPublished(null);
    try {
      const data = await callMiniApp({
        action: "draft",
        ...payloadFor(contact),
        prompt,
        ctaUrl,
        design,
        blueprint: draft ? null : blueprint,
        brain: activeTasks,
      });
      setDraft({
        spec: data.spec,
        brain: Array.isArray(data.brain) ? data.brain : [],
        brandStyle: data.brandStyle || null,
        contactId: contact.id,
        business: contact.business_name,
      });
      setDraftVersion((v) => v + 1);
    } catch (err) {
      setTestMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    if (!prompt.trim()) {
      setMessage("Describe what the Mini-App should do.");
      return;
    }
    const spec = draft?.spec || blueprint;
    if (!spec) {
      setMessage(
        "Build the Mini-App for a test contact first, so every contact gets this structure.",
      );
      return;
    }
    setBusy("save");
    setMessage("");
    const now = Date.now();
    const payload = {
      type: "miniApp",
      name: name.trim() || spec.title || "Mini-App",
      token: effectiveToken,
      prompt: prompt.trim(),
      ctaUrl: ctaUrl.trim(),
      blueprint: spec,
      design,
      brain: activeTasks.length ? activeTasks : null,
      altText: "",
      maxWidth: 600,
      align: "center",
      updatedAt: now,
    };
    try {
      const basePath = `/World/${userId}/settings/imageLab`;
      if (savedId) {
        await update(ref(database, `${basePath}/${savedId}`), payload);
      } else {
        const newRef = push(ref(database, basePath));
        await update(newRef, { ...payload, createdAt: now });
        setSavedId(newRef.key);
      }
      setBlueprint(spec);
      setSavedDesign(design);
      setToken(effectiveToken);
      setMessage(
        "Saved. Put the tag into an email and every contact gets their own version.",
      );
    } catch (err) {
      setMessage(`Error saving: ${err.message || err}`);
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    if (!savedId || !draft) return;
    const target =
      contacts.find((c) => c.id === draft.contactId) ||
      (pool || []).find((c) => c.id === draft.contactId);
    if (!target) return;
    setBusy("publish");
    setPublishError("");
    try {
      const data = await callMiniApp({
        action: "publish",
        ...payloadFor(target),
        token: effectiveToken,
        spec: draft.spec,
      });
      setPublished({ ...data, business: target.business_name });
      if (data.imageUrl && !existing?.previewImageUrl) {
        update(ref(database, `/World/${userId}/settings/imageLab/${savedId}`), {
          previewImageUrl: data.imageUrl,
        }).catch(() => {});
      }
    } catch (err) {
      setPublishError(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    if (
      !savedId ||
      !window.confirm("Delete this Mini-App? Apps already sent keep working.")
    )
      return;
    await remove(
      ref(database, `/World/${userId}/settings/imageLab/${savedId}`),
    );
    onExit();
  };

  const copy = (text, key) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1600);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  const savedMatchesDraft =
    !!savedId &&
    !!draft &&
    blueprint === draft.spec &&
    JSON.stringify(savedDesign) === JSON.stringify(design);

  const steps = [
    !savedId && {
      key: "template",
      title: "Template",
      sub: "Click one to try the live demo for free. When you build it for a contact, every number and text is made for that business.",
      body: (
        <div
          className="mae-templates"
          role="radiogroup"
          aria-label="Mini-App templates"
        >
          {TEMPLATES.map((t) => (
            <button
              type="button"
              key={t.id}
              role="radio"
              aria-checked={t.id === templateId}
              className={`mae-template ${t.id === templateId ? "active" : ""}`}
              onClick={() => pickTemplate(t)}
            >
              <img src={coverOf(t)} alt="" loading="lazy" />
              <span className="mae-templateBody">
                <span className="mae-templateTop">
                  <b>{t.name}</b>
                  <em>{t.industry}</em>
                </span>
                <span className="mae-templatePitch">{t.pitch}</span>
              </span>
              {t.id === templateId && (
                <i
                  className="fa-solid fa-circle-check mae-templateCheck"
                  aria-hidden="true"
                />
              )}
            </button>
          ))}
        </div>
      ),
    },
    {
      key: "brief",
      title: "What the app does",
      sub: "The AI only assembles safe building blocks (inputs, formulas, charts, tables, checklists). It never writes code.",
      body: (
        <div className="mae-fields">
          <label className="il-field">
            <span className="il-fieldLabel">Briefing</span>
            <textarea
              className="il-aiPromptInput"
              rows={5}
              value={prompt}
              placeholder="E.g. an ROI calculator: rooms, occupancy and average rate → how much commission they save per year with direct bookings."
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
          <label className="il-field">
            <span className="il-fieldLabel">
              Button link <em>(optional)</em>
            </span>
            <input
              type="text"
              value={ctaUrl}
              placeholder="https://calendly.com/you/15min or mailto:you@company.com"
              onChange={(e) => setCtaUrl(e.target.value)}
            />
            <span className="il-fieldHint">
              The button on every app always uses this link, never one the AI
              makes up.
            </span>
          </label>
        </div>
      ),
    },
    {
      key: "brain",
      title: "Content brain (optional)",
      sub: "For apps that need real thinking. Before the app is built, these AIs work out the content for each contact: research, texts, numbers. The app builder then only turns that into layout and design.",
      body: <BrainPanel tasks={brainTasks} onChange={setBrainTasks} />,
    },
    {
      key: "test",
      title: "Design and live test",
      sub: "Style it on the right and build it for a real contact. The preview updates while you change the design.",
      body: (
        <div className="mae">
          <div className="mae-previewCol">
            <div className="mae-browser">
              <div className="mae-browserBar">
                <span />
                <span />
                <span />
                <em>
                  {published
                    ? hostOf(published.linkUrl) +
                      new URL(published.linkUrl).pathname
                    : showingDemo
                      ? `Demo with example data · ${template.demo.business}`
                      : draft
                        ? `Built for ${draft.business}`
                        : "bonusclub.link/miniapp/…"}
                </em>
              </div>
              <div className="mae-browserBody">
                {busy === "build" ? (
                  <div className="mae-empty">
                    <i className="fa-solid fa-wand-magic-sparkles fa-fade" />
                    <span>Building the app for {contact?.business_name}…</span>
                  </div>
                ) : previewSpec ? (
                  <MiniAppRenderer key={draftVersion} spec={previewSpec} />
                ) : (
                  <div className="mae-empty">
                    <i className="fa-solid fa-calculator" />
                    <span>
                      Pick a template or describe the app, then build it for a
                      contact. The live app appears here.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {draft?.brain?.length > 0 && (
              <details className="mae-brainOut">
                <summary>
                  <i className="fa-solid fa-brain" />
                  Content the brain prepared for {draft.business}
                  <span>
                    {draft.brain.length}{" "}
                    {draft.brain.length === 1 ? "task" : "tasks"}
                  </span>
                </summary>
                {draft.brain.map((r) => (
                  <div className="mae-brainResult" key={r.id}>
                    <div className="mae-brainResultHead">
                      <b>{r.label}</b>
                      <span>
                        {
                          (BRAIN_MODELS.find((m) => m.id === r.model) || {})
                            .engine
                        }
                        {r.cached ? " · reused, free" : ""}
                      </span>
                    </div>
                    {r.error ? (
                      <p className="mae-brainError">
                        {r.error} Credits refunded.
                      </p>
                    ) : (
                      <p>{r.text}</p>
                    )}
                  </div>
                ))}
              </details>
            )}

            {draft && (
              <div className="mae-publish">
                <div>
                  <strong>Send it to {draft.business} only?</strong>
                  <span>
                    {savedMatchesDraft
                      ? "Publishes exactly this app. Emails to this contact will use it."
                      : "Save first, then publish exactly this app for this contact."}
                  </span>
                </div>
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={publish}
                  disabled={!!busy || !savedMatchesDraft}
                >
                  <i
                    className={`fa-solid ${busy === "publish" ? "fa-spinner fa-spin" : "fa-link"}`}
                  />
                  {busy === "publish"
                    ? "Publishing…"
                    : `Publish for ${draft.business}`}
                </button>
              </div>
            )}
            {publishError && (
              <div className="il-msg mae-inlineMsg">{publishError}</div>
            )}

            {published && (
              <div className="mae-published">
                <img
                  src={published.imageUrl}
                  alt={published.altText || "Mini-App preview"}
                />
                <div className="mae-linkRow">
                  <code>{published.linkUrl}</code>
                  <button
                    type="button"
                    className="wr-demoShuffle"
                    onClick={() => copy(published.linkUrl, "link")}
                  >
                    <i
                      className={
                        copied === "link"
                          ? "fa-solid fa-check"
                          : "fa-regular fa-copy"
                      }
                    />{" "}
                    Copy link
                  </button>
                  <a
                    className="wr-demoShuffle"
                    href={published.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" />{" "}
                    Open
                  </a>
                </div>
                <p className="wr-demoNote">
                  This is the card {published.business} sees in the email. It is
                  deleted 60 days after the last email that links to it
                  {published.expiresAt
                    ? ` (currently ${new Date(published.expiresAt).toLocaleDateString()})`
                    : ""}
                  .
                </p>
              </div>
            )}
          </div>

          <aside className="mae-side">
            <div className="wr-demoSide">
              <div className="wr-demoSideHead">
                <span className="il-fieldLabel">Test on a real contact</span>
                <button
                  type="button"
                  className="wr-demoShuffle"
                  onClick={() => {
                    setContacts(pickRandomContacts(pool || []));
                    setContactId(null);
                  }}
                  disabled={!pool || !pool.length}
                >
                  <i className="fa-solid fa-shuffle" /> Others
                </button>
              </div>
              {pool === null ? (
                <p className="wr-demoEmpty">Loading contacts…</p>
              ) : contacts.length === 0 ? (
                <p className="wr-demoEmpty">
                  No contacts yet. Add one to build the app for a real business.
                </p>
              ) : (
                <div
                  className="wr-demoContacts"
                  role="radiogroup"
                  aria-label="Test contact"
                >
                  {contacts.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      role="radio"
                      aria-checked={c.id === contactId}
                      className={`wr-demoContact ${c.id === contactId ? "active" : ""}`}
                      onClick={() => setContactId(c.id)}
                    >
                      <b>{c.business_name}</b>
                      <span>
                        {[c.lead_city, hostOf(c.lead_website)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="altBtn altBtn--save mae-build"
                onClick={build}
                disabled={!!busy || !contact}
              >
                <i
                  className={`fa-solid ${busy === "build" ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"}`}
                />
                {busy === "build"
                  ? "Building…"
                  : draft
                    ? "Build again"
                    : "Build Mini-App"}
              </button>
              <p className="wr-demoNote">
                {contact
                  ? `${credits} credits, for ${contact.business_name}. Built like a real send from their research.`
                  : "Pick a contact first. Built like a real send from their research."}
              </p>
              {testMessage && (
                <div className="il-msg mae-inlineMsg">{testMessage}</div>
              )}
            </div>
            <DesignPanel
              design={design}
              onChange={setDesign}
              spec={previewSpec}
              brandStyle={draft?.brandStyle}
              brandFor={draft?.business}
            />
          </aside>
        </div>
      ),
    },
    {
      key: "output",
      title: "Output",
      sub: "Name and tag. Put the tag into an email and every recipient gets their own app as a clickable preview.",
      body: (
        <div className="wr-outputGrid">
          <label className="il-field">
            <span className="il-fieldLabel">Mini-App name</span>
            <input
              type="text"
              value={name}
              placeholder="Direct booking ROI"
              onChange={(e) => {
                setName(e.target.value);
                if (!tokenTouched)
                  setToken(`app_${slugifyToken(e.target.value)}`);
              }}
            />
          </label>
          <label className="il-field">
            <span className="il-fieldLabel">Tag</span>
            <input
              type="text"
              className="mae-mono"
              value={token}
              placeholder="app_roi"
              onChange={(e) => {
                setToken(slugifyToken(e.target.value));
                setTokenTouched(true);
              }}
            />
          </label>
          <div className="wr-outputWide mae-tagRow">
            <button
              type="button"
              className="mae-tag"
              onClick={() => copy(`{{image:${effectiveToken}}}`, "tag")}
            >
              <code>{`{{image:${effectiveToken}}}`}</code>
              <i
                className={
                  copied === "tag" ? "fa-solid fa-check" : "fa-regular fa-copy"
                }
              />
            </button>
            <span className="il-fieldHint">
              {credits} credits per new contact, resends are free. No BonusClub
              branding, deleted 60 days after the last email.
            </span>
          </div>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="il wr mae-wrap">
      <div className="il-editorBar">
        <span className="il-editorTitle">
          {savedId ? "Edit Mini-App" : "New Mini-App"}
        </span>
      </div>

      {steps.map((step, index) => (
        <Step key={step.key} n={index + 1} title={step.title} sub={step.sub}>
          {step.body}
        </Step>
      ))}

      {message && <div className="il-msg">{message}</div>}

      <div className="il-editorActions">
        <div
          className="il-costMeter"
          title="Credits per contact. Resends to the same contact reuse their app for free."
        >
          <span className="il-costMeterIcon">
            <i className="fa-solid fa-coins" />
          </span>
          <span className="il-costMeterBody">
            <span className="il-costMeterMain">
              {credits} <em>credits / contact</em>
            </span>
            <span className="il-costMeterMeta">
              {activeTasks.length
                ? `incl. ${activeTasks.length} content ${activeTasks.length === 1 ? "task" : "tasks"} · resends free`
                : "incl. research · resends free"}
            </span>
          </span>
        </div>
        <div className="il-editorActionsBtns">
          {savedId && (
            <button
              type="button"
              className="il-btnGhost wr-delete"
              onClick={handleDelete}
              disabled={!!busy}
            >
              <i className="fa-regular fa-trash-can" /> Delete
            </button>
          )}
          <button
            type="button"
            className="altBtn altBtn--petrol"
            onClick={() => copy(`{{image:${effectiveToken}}}`, "tag")}
          >
            <i
              className={
                copied === "tag" ? "fa-solid fa-check" : "fa-regular fa-copy"
              }
            />{" "}
            Copy tag
          </button>
          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={save}
            disabled={!!busy}
          >
            {busy === "save"
              ? "Saving…"
              : savedId
                ? "Save changes"
                : "Save Mini-App"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniAppEditor;
